import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Convert any image file to WebP using canvas
// Resizes to max 1200px wide, exports at 85% quality
// Typical result: 5MB JPEG → 400-700KB WebP
async function convertToWebP(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      // Cap width at 1200px, maintain aspect ratio
      const MAX_W = 1200
      let w = img.width
      let h = img.height
      if (w > MAX_W) {
        h = Math.round((h * MAX_W) / w)
        w = MAX_W
      }

      const canvas = document.createElement('canvas')
      canvas.width  = w
      canvas.height = h

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, w, h)

      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Canvas conversion failed')),
        'image/webp',
        0.85  // 85% quality — good balance of size vs visual quality
      )
    }

    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')) }
    img.src = url
  })
}

export default function ImageUpload({ value, onChange }) {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress]   = useState('')  // 'converting' | 'uploading' | ''
  const [error, setError]         = useState('')
  const [dragOver, setDragOver]   = useState(false)
  const [sizeInfo, setSizeInfo]   = useState(null) // { original, converted }
  const inputRef = useRef()

  async function handleFile(file) {
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file.'); return }
    if (file.size > 15 * 1024 * 1024) { setError('Image must be under 15MB.'); return }
    setError('')
    setSizeInfo(null)

    const originalKB = Math.round(file.size / 1024)

    // Step 1: Convert to WebP
    setProgress('converting')
    let webpBlob
    try {
      webpBlob = await convertToWebP(file)
    } catch (err) {
      // Fallback: use original if conversion fails
      console.warn('WebP conversion failed, using original:', err)
      webpBlob = file
    }

    const convertedKB = Math.round(webpBlob.size / 1024)
    setSizeInfo({ original: originalKB, converted: convertedKB })

    // Step 2: Upload WebP to Supabase
    setProgress('uploading')
    const path = `listings/${Date.now()}-${Math.random().toString(36).slice(2)}.webp`

    const { error: uploadErr } = await supabase.storage
      .from('listing-images')
      .upload(path, webpBlob, {
        cacheControl: '3600',
        upsert:       false,
        contentType:  'image/webp',
      })

    setProgress('')

    if (uploadErr) {
      setError('Upload failed: ' + uploadErr.message)
      setSizeInfo(null)
      return
    }

    const { data: { publicUrl } } = supabase.storage
      .from('listing-images')
      .getPublicUrl(path)

    onChange(publicUrl)
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer.files[0])
  }

  const uploading_state = !!progress

  return (
    <div>
      {/* Preview */}
      {value && (
        <div style={{ position: 'relative', marginBottom: 10, borderRadius: 10, overflow: 'hidden', height: 160 }}>
          <img src={value} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <button
            onClick={() => { onChange(''); setSizeInfo(null) }}
            style={{
              position: 'absolute', top: 8, right: 8,
              background: 'rgba(0,0,0,.6)', color: '#fff', border: 'none',
              width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
              fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
          {/* Size saved badge */}
          {sizeInfo && sizeInfo.converted < sizeInfo.original && (
            <div style={{
              position: 'absolute', bottom: 8, left: 8,
              background: 'rgba(5,150,105,.9)', backdropFilter: 'blur(4px)',
              color: '#fff', fontSize: 11, fontWeight: 600,
              padding: '3px 8px', borderRadius: 5, fontFamily: 'var(--mono)',
            }}>
              ✓ {sizeInfo.original}KB → {sizeInfo.converted}KB WebP
            </div>
          )}
        </div>
      )}

      {/* Drop zone */}
      {!value && (
        <div
          onClick={() => !uploading_state && inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragOver ? 'var(--blue)' : 'var(--gray-300)'}`,
            borderRadius: 10,
            padding: '28px 20px',
            textAlign: 'center',
            cursor: uploading_state ? 'default' : 'pointer',
            background: dragOver ? '#eff6ff' : 'var(--gray-50)',
            transition: 'all .15s',
          }}
        >
          {progress === 'converting' ? (
            <div style={{ color: 'var(--blue)', fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🔄</div>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Converting to WebP…</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Compressing your image for faster loading</div>
            </div>
          ) : progress === 'uploading' ? (
            <div style={{ color: 'var(--blue)', fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>⬆️</div>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Uploading…</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Almost there</div>
            </div>
          ) : (
            <>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="1.5" style={{ marginBottom: 8 }}>
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
                Drop a photo here or click to browse
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', lineHeight: 1.6 }}>
                JPG, PNG, WebP · Max 15MB<br/>
                <strong style={{ color: 'var(--gray-500)' }}>Recommended: 1200 × 800px</strong> (landscape works best)<br/>
                <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ Auto-converted to WebP for faster loading</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Or paste URL */}
      {!value && !uploading_state && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', textAlign: 'center', marginBottom: 8 }}>
            — or paste an image URL —
          </div>
          <input
            type="url"
            placeholder="https://example.com/photo.jpg"
            onChange={e => {
              if (e.target.value.match(/^https?:\/\/.+\.(jpg|jpeg|png|webp|gif)/i)) {
                onChange(e.target.value)
              }
            }}
            style={{ width: '100%', border: '1.5px solid var(--gray-200)', borderRadius: 8, padding: '9px 14px', fontSize: 13, outline: 'none' }}
          />
        </div>
      )}

      {error && (
        <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{error}</div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])}
      />
    </div>
  )
}

